// src/components/SystemAdminDashboard.tsx
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { createData, deleteData, fetchData, uploadData } from "api/apiHelpers";
import api from "api/api";
import ExpandToggle from "components/layout/ExpandToggle";
import GlassBox from "components/layout/GlassBox";
import { NeumorphicCard } from "components/layout/NeumorphicCard";
import { EnvironmentFilterProvider, useEnvironmentFilter } from "hooks/useEnvironmentFilter";
import { useTutorial } from "hooks/useOnboardingTutorial";
import { useSnackbar } from "notistack";
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Environment, PlatformUser, RootState } from "types";
import logger from "utils/logger";
import { useTranslation } from "../../i18n";
import { setBreadcrumbState } from "../../redux/breadcrumbSlice";
import { setCompanyId } from "../../redux/companySlice";
import {
  persistUiPreferences,
  setUiPreferences,
} from "../../redux/authSlice";
import type { AppDispatch } from "../../redux/store";
import { anonymizeName } from "../PatientDetails/panels/DataPanel";
import AddPatientFormV2 from "./add_patient_v2/AddPatientFormV2";
import DashboardBackButton from "./common/DashboardBackButton";
import useCachedEnvSelection from "../../hooks/useCachedEnvSelection";
import AnalyticsDashboard from "./AnalyticsDashboard";
import DashboardChat from "./DashboardChat";
import type { DashboardChatHandle } from "./DashboardChat";
import { CohortSummaryCards } from "./charts";
import { TypewriterTitle } from "./CompanyCreationTemplate";
import EnvironmentFilterChips from "./EnvironmentFilterChips";
import VideoGlassContainer from "./VideoGlassContainer";
import { athenaColors } from "../../theme/palette";
import {
  glassControl,
  glassInputSx,
  glassListSx,
} from "../../theme/glassTokens";
import {
  athenaBackdropSx,
  athenaDarkActionsSx,
  athenaDarkContentSx,
  athenaDarkPaperSx,
  athenaDarkSecondaryButtonSx,
  athenaDarkTitleSx,
} from "../layout/AthenaDialogStyles";
import WelcomeHeroCard from "./WelcomeHeroCard";
import type { CohortSummary } from "hooks/useAnalytics";

const MAX_PATIENTS_DISPLAY = 20;

// ---------------------------------------------------------------------------
// Hero band: env chips + KPI cards (populated state)
// Must be rendered inside <EnvironmentFilterProvider>.
// ---------------------------------------------------------------------------
// Mac reports the command key, everything else Ctrl. Computed once at module
// load: the platform does not change mid-session.
const SHORTCUT_KEYS: string[] =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? ["\u2318", "K"]
    : ["Ctrl", "K"];

const DashboardHeroBand: React.FC = () => {
  const { selectedIds: envIds, isFiltered: envIsFiltered } = useEnvironmentFilter();
  const [cohort, setCohort] = React.useState<CohortSummary | null>(null);
  const [bandLoading, setBandLoading] = React.useState(true);

  const envQs =
    envIds && envIds.length > 0 ? `?environments=${envIds.join(",")}` : "";

  React.useEffect(() => {
    setBandLoading(true);
    fetchData<CohortSummary>(`/insights/cohort-summary/${envQs}`)
      .then(setCohort)
      .catch(() => setCohort(null))
      .finally(() => setBandLoading(false));
  }, [envQs]);

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ mb: 1 }}>
        <EnvironmentFilterChips />
      </Box>
      <CohortSummaryCards
        data={cohort}
        loading={bandLoading}
        scoped={envIsFiltered}
      />
    </Box>
  );
};

const SystemAdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const tutorial = useTutorial();
  const canCreatePatients = true;

  // Expandable section states
  const [expandedAddPatient, setExpandedAddPatient] = useState(false);
  const [expandedManage, setExpandedManage] = useState(true);

  // V1/V2 dev toggle for the AddPatientForm refactor.
  // Persisted in localStorage so it survives page reloads during the
  // hand-driven A/B period. Default = V1 (Stable). The real production
  // Joan, 2026-05-27: V2 is the default and the only form path the
  // dashboard exposes. V1 code lives in components/dashboards/AddPatientForm.tsx
  // and remains importable for fallback, but the toggle UI is gone.
  const useFormV2 = true;

  // NL query expanded state (results shown in VideoGlassContainer)
  const [nlExpanded, setNlExpanded] = useState(false);
  const [nlFollowupInput, setNlFollowupInput] = useState("");
  const [nlFollowups, setNlFollowups] = useState<string[]>([]);
  const nlChatRef = useRef<DashboardChatHandle | null>(null);

  const handleNlActiveChange = React.useCallback((active: boolean) => {
    setNlExpanded(active);
  }, []);

  const handleNlFollowupsChange = React.useCallback((followups: string[]) => {
    setNlFollowups(followups);
  }, []);

  const handleNlDismiss = React.useCallback(() => {
    setNlExpanded(false);
    setNlFollowupInput("");
    setNlFollowups([]);
    nlChatRef.current?.clearAll();
  }, []);

  const handleNlFollowupSubmit = React.useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    const q = nlFollowupInput.trim();
    if (!q) return;
    nlChatRef.current?.submitFollowup(q);
    setNlFollowupInput("");
  }, [nlFollowupInput]);

  const handleNlChipFollowup = React.useCallback((suggestion: string) => {
    nlChatRef.current?.submitFollowup(suggestion);
    setNlFollowupInput("");
  }, []);

  // Manage Patients state
  const [patients, setPatients] = useState<PlatformUser[]>([]);
  const [patientCount, setPatientCount] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The first character makes isSearchActive flip true, which unmounts the
  // big "welcome / analytics" siblings AND opens the search Collapse — that
  // layout shift can pull focus off the input mid-type, so the next chars
  // never register. This ref + focus-restoration effect (below) brings focus
  // straight back to the input whenever the user is mid-search.
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Per-patient raw-name reveals in the search list. Each click of the eye
  // hits /patients/<id>/reveal-name/ which writes a PatientNameReveal audit row.
  const [revealedNames, setRevealedNames] = useState<Record<string, string>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);
  // True while a search is in-flight (debounce wait + request). Lets the
  // results panel show a spinner instead of "No patients found" before the
  // first response lands.
  const [searching, setSearching] = useState(false);

  const handleRevealPatientName = useCallback(
    async (e: React.MouseEvent, p: PlatformUser) => {
      e.stopPropagation(); // don't trigger the row's navigate-to-details click
      if (revealedNames[p.id] !== undefined) {
        // Already revealed — hide it.
        setRevealedNames((s) => {
          const next = { ...s };
          delete next[p.id];
          return next;
        });
        return;
      }
      setRevealingId(p.id);
      try {
        const resp = await api.get<{ name: string; external_id: string }>(
          `/patients/${p.id}/reveal-name/`,
        );
        setRevealedNames((s) => ({ ...s, [p.id]: resp.data.name || "" }));
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        enqueueSnackbar(
          status === 403
            ? t(
                "patient.reveal_forbidden",
                "You don't have permission to view this patient's raw name.",
              )
            : t(
                "patient.reveal_failed",
                "Could not reveal the name — the server returned an error.",
              ),
          { variant: "error" },
        );
      } finally {
        setRevealingId(null);
      }
    },
    [revealedNames, enqueueSnackbar, t],
  );

  // New Patient form state
  const [newPatient, setNewPatient] = useState<Partial<PlatformUser>>({
    name: "",
    description: "",
  });
  const [availableEnvironments, setAvailableEnvironments] = useState<
    Environment[]
  >([]);
  // Joan, 2026-05-27: per-user cached selection with fault-tolerant fallback.
  // Removed envs silently drop from the cache; if nothing usable remains, the
  // hook prefers the consortium env (when Q4 lands and exposes `kind`) and
  // otherwise the first available env.
  const preferredEnvId =
    availableEnvironments.find(
      (e) => (e as Environment & { kind?: string }).kind === "consortium",
    )?.id ?? null;
  // Write-through to the DB (cursor_logs/042): optimistically merge into the
  // Redux user, then fire the debounced PATCH. localStorage is handled inside
  // the hook. Reading ui_preferences via a ref keeps this callback stable so
  // the hook's setter identity doesn't churn on every persist.
  const uiPreferencesRef = useRef<Record<string, unknown>>(
    (currentUser?.ui_preferences as Record<string, unknown>) || {},
  );
  useEffect(() => {
    uiPreferencesRef.current =
      (currentUser?.ui_preferences as Record<string, unknown>) || {};
  }, [currentUser?.ui_preferences]);
  const handlePersistEnvPrefs = useCallback(
    (next: { selectedEnvIds: string[]; seenEnvIds: string[] }) => {
      const merged = {
        ...uiPreferencesRef.current,
        patient_create_env_ids: next.selectedEnvIds,
        patient_create_seen_env_ids: next.seenEnvIds,
      };
      dispatch(setUiPreferences(merged));
      (dispatch as AppDispatch)(persistUiPreferences(merged));
    },
    [dispatch],
  );
  const [selectedEnvIds, setSelectedEnvIds] = useCachedEnvSelection({
    userId: currentUser?.id ?? null,
    availableEnvironments,
    preferredEnvId,
    dbSelectedEnvIds:
      (currentUser?.ui_preferences?.patient_create_env_ids as
        | string[]
        | undefined) ?? null,
    dbSeenEnvIds:
      (currentUser?.ui_preferences?.patient_create_seen_env_ids as
        | string[]
        | undefined) ?? null,
    onPersist: handlePersistEnvPrefs,
  });

  // Analytics refresh state
  const [analyticsRefresh, setAnalyticsRefresh] = useState(0);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<PlatformUser | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  // Keyboard navigation index for search results (-1 = none selected)
  const [searchHighlightIdx, setSearchHighlightIdx] = useState(-1);

  // Search-driven mode: analytics vs patient list
  const isSearchActive = searchQuery.trim().length > 0;

  const containerRef = useRef<HTMLDivElement>(null);

  // (Removed 2026-06-03 — see cursor_logs/036 §16: portaling the search row
  // into #dashboard-floating-header caused React to unmount/remount the
  // TextField every time isSearchActive flipped, which ate keystrokes. The
  // search row now stays inline and floats visually via position:fixed when
  // V2 form is active. The floating-header DOM slot is now unused for this
  // purpose.)

  // Make the outer glass container invisible when the dashboard is empty
  useEffect(() => {
    const el = containerRef.current?.closest("[data-glass-container]") as HTMLElement | null;
    if (!el) return;
    if (patientCount === 0 && !loading) {
      el.dataset.emptyDashboard = "true";
    } else {
      delete el.dataset.emptyDashboard;
    }
    return () => { delete el.dataset.emptyDashboard; };
  }, [patientCount, loading]);

  // Joan, 2026-05-27: hide the outer container chrome when the dashboard
  // hosts a single-focus form (V2 patient creation or NL query). The
  // default dashboard view keeps the chrome so analytics + lists still
  // sit on a proper card. V1 patient creation is unaffected — its own
  // VideoGlassContainer wrapper provides the chrome.
  useEffect(() => {
    const el = containerRef.current?.closest("[data-glass-container]") as HTMLElement | null;
    if (!el) return;
    const shouldFloat = (expandedAddPatient && useFormV2) || nlExpanded;
    if (shouldFloat) {
      el.dataset.floatingContent = "true";
    } else {
      delete el.dataset.floatingContent;
    }
    return () => { delete el.dataset.floatingContent; };
  }, [expandedAddPatient, useFormV2, nlExpanded]);

  // Collapse add-patient form when search becomes active.
  // Guarded: only call setExpandedAddPatient when it would actually change,
  // so a noop on every keystroke doesn't risk a parent re-render that strips
  // focus from the search input on the first character typed.
  useEffect(() => {
    if (isSearchActive && expandedAddPatient) {
      setExpandedAddPatient(false);
    }
  }, [isSearchActive, expandedAddPatient]);

  // Restore focus to the search input if it was lost during a re-render.
  // Triggered after every searchQuery / patients update so even if the layout
  // shift unmounts/remounts a sibling that pulls focus, the input gets it back
  // before the user's next keystroke. No-op when the input is already focused.
  useEffect(() => {
    if (!searchQuery) return;
    if (document.activeElement === searchInputRef.current) return;
    searchInputRef.current?.focus();
  }, [searchQuery, patients]);

  // Ctrl/Cmd+K focuses the patient search. The affordance is advertised in the
  // field itself (see the endAdornment below), so it is a real shortcut rather
  // than a decorative chip.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset to default dashboard view when logo is clicked (even if already on /dashboard)
  useEffect(() => {
    if ((location.state as { resetView?: number })?.resetView) {
      setExpandedAddPatient(false);
      setSearchQuery("");
      handleNlDismiss();
    }
  }, [(location.state as { resetView?: number })?.resetView]);

  // Fetch available environments
  useEffect(() => {
    (async () => {
      try {
        const envs = await fetchData<Environment[]>("/environments/");
        setAvailableEnvironments(envs);
      } catch (err) {
        logger.error("Error fetching environments:", err);
      }
    })();
  }, []);

  // Check if any patients exist (lightweight: fetch 1 record)
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchData<{ count: number; results: PlatformUser[] }>(
          "/patients/?page_size=1"
        );
        setPatientCount(data.count);
      } catch (error) {
        logger.error(error);
      }
      setLoading(false);
    })();
  }, [analyticsRefresh]);

  // Server-side search with debounce. The AbortController + lastQueryRef
  // together kill the classic race: typing fast fires several requests, and
  // without these guards an older response (e.g. for "ai") could land AFTER a
  // newer one (e.g. "aisha") and overwrite valid results with stale/empty
  // ones — exactly the "I search, get nothing, retype, then get results"
  // behaviour Joan reported.
  const searchAbortRef = useRef<AbortController | null>(null);
  const lastQueryRef = useRef<string>("");
  const runSearch = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      lastQueryRef.current = trimmed;
      if (!trimmed) {
        if (searchAbortRef.current) searchAbortRef.current.abort();
        setPatients([]);
        setSearching(false);
        return;
      }
      if (searchAbortRef.current) searchAbortRef.current.abort();
      const ctrl = new AbortController();
      searchAbortRef.current = ctrl;
      try {
        const resp = await api.get<{ count: number; results: PlatformUser[] }>(
          `/patients/?search=${encodeURIComponent(trimmed)}&page_size=${MAX_PATIENTS_DISPLAY}`,
          { signal: ctrl.signal },
        );
        // If a newer search has started in the meantime, drop this response.
        // (Don't clear `searching` here — the newer in-flight request will.)
        if (lastQueryRef.current !== trimmed) return;
        setPatients(resp.data.results);
        setSearching(false);
        // Do NOT update patientCount here — search results count must not
        // overwrite the total count that drives empty-state / WelcomeHeroCard.
      } catch (error: unknown) {
        // Aborts are expected on every fast keystroke — ignore them and
        // leave `searching` alone so the spinner persists for the new query.
        const name = (error as { name?: string; code?: string })?.name;
        const code = (error as { name?: string; code?: string })?.code;
        if (name === "CanceledError" || code === "ERR_CANCELED") return;
        logger.error(error);
        if (lastQueryRef.current === trimmed) setSearching(false);
      }
    },
    []
  );

  // Handlers
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchHighlightIdx(-1);
    // Spinner ON as soon as the user types — covers the debounce window and
    // the network round-trip; runSearch flips it back off when results land.
    setSearching(value.trim().length > 0);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => runSearch(value), 300);
  };

  // Keyboard navigation for search results
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (!isSearchActive || patientsToShow.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlightIdx((prev) =>
        prev < patientsToShow.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlightIdx((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = searchHighlightIdx >= 0 ? searchHighlightIdx : 0;
      if (patientsToShow[idx]) {
        handlePatientClick(patientsToShow[idx]);
      }
    }
  };

  const patientsToShow = patients.slice(0, MAX_PATIENTS_DISPLAY);

  const handlePatientClick = (patient: PlatformUser) => {
    dispatch(setCompanyId(Number(patient.id)));
    dispatch(
      setBreadcrumbState({
        platformUser: { id: Number(patient.id), name: patient.name },
      })
    );
    navigate(`/patients/${patient.id}/details`);
  };

  const handleCreatePatient = async (files: File[] = []): Promise<boolean> => {
    if (!newPatient.name?.trim()) {
      enqueueSnackbar(t("patients.create_name_required", "Name is required"), {
        variant: "warning",
      });
      return false;
    }
    try {
      setLoading(true);
      // During the tutorial the user may not have selected any environments.
      // Fall back to the first available environment so the backend doesn't
      // reject the request with "No valid environments provided."
      const envIds =
        selectedEnvIds.length > 0
          ? selectedEnvIds
          : tutorial.isActive && availableEnvironments.length > 0
            ? [String(availableEnvironments[0].id)]
            : selectedEnvIds;

      const payload: Record<string, unknown> = {
        ...newPatient,
        environments: envIds,
        ...(tutorial.isActive ? { is_tutorial_patient: true } : {}),
      };
      const patient: PlatformUser = await createData("/patients/", payload);

      // Store the patient reference in tutorial context so the search step
      // can greet the user by name.
      if (tutorial.isActive && newPatient.name) {
        tutorial.setTutorialPatient(newPatient.name.trim(), Number(patient.id));
      }

      // Upload initial documents if any
      if (files.length > 0) {
        enqueueSnackbar(
          t("patients.uploading_files", `Uploading ${files.length} file(s)...`),
          { variant: "info" }
        );
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("patient", patient.id);
          try {
            await uploadData(`/patient-documents/`, formData);
          } catch (uploadError) {
            logger.error(`Error uploading ${file.name}:`, uploadError);
          }
        }
      }

      enqueueSnackbar(
        t("patients.success_message", "Patient created successfully!"),
        { variant: "success" }
      );
      setNewPatient({ name: "", description: "" });
      // Joan, 2026-05-27: keep the env selection across creations so the
      // next patient lands in the same env without re-picking.
      setExpandedAddPatient(false);
      setPatients((prev) => [...prev, patient]);
      setAnalyticsRefresh((prev) => prev + 1);
      navigate(`/patients/${patient.id}/details`);
      return true;
    } catch (error) {
      enqueueSnackbar(t("patients.error_message", "Error creating patient"), {
        variant: "error",
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete patient handlers
  const handleDeleteClick = (e: React.MouseEvent, patient: PlatformUser) => {
    e.stopPropagation();
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!patientToDelete) return;
    try {
      setDeleting(true);
      await deleteData(`/patients/${patientToDelete.id}/`);
      setPatients((prev) => prev.filter((p) => p.id !== patientToDelete.id));
      setPatientCount((prev) => Math.max((prev ?? 1) - 1, 0));
      enqueueSnackbar(
        t("patients.delete_success", "Patient deleted successfully"),
        { variant: "success" }
      );
      setAnalyticsRefresh((prev) => prev + 1);
    } catch (error) {
      enqueueSnackbar(t("patients.delete_error", "Error deleting patient"), {
        variant: "error",
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPatientToDelete(null);
  };

  // Loading state
  if (loading && patientCount === null) {
    return (
      <Box
        tabIndex={0}
        sx={{
          width: "100%",
          maxWidth: "500px",
          minHeight: "100vh",
          bgcolor: "transparent",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: theme.spacing(2),
          mx: "auto",
        }}
      >
        <CircularProgress />
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            animation: "fadeIn 2s ease-in-out",
            pt: theme.spacing(5),
          }}
        >
          <TypewriterTitle
            text={t("form.loading", "Loading...")}
            variant="h6"
          />
        </Box>
      </Box>
    );
  }

  return (
    <EnvironmentFilterProvider environments={availableEnvironments}>
      <Container
        ref={containerRef}
        disableGutters
        maxWidth={false}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
          // No outer card: the dashboard's sections carry their own glass on
          // the page ground (see DashboardGround). This only supplies the
          // gutters.
          p: { xs: 2, md: 2.5, lg: 3 },
        }}
      >
        {/* ----------------------------------------------------------------- */}
        {/* NL expanded header: back arrow + follow-up search bar + chips     */}
        {/* ----------------------------------------------------------------- */}
        {nlExpanded && (
          <Box sx={{ mb: 1.5, flexShrink: 0 }}>
            {/* Row: back arrow + search bar */}
            <Box
              component="form"
              onSubmit={handleNlFollowupSubmit}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <DashboardBackButton prominent onClick={handleNlDismiss} />
              <TextField
                value={nlFollowupInput}
                onChange={(e) => setNlFollowupInput(e.target.value)}
                placeholder={t(
                  "dashboard_chat.follow_up_placeholder",
                  "Ask a follow-up question..."
                )}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: 20,
                          color: athenaColors.periwinkle,
                          opacity: 1,
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ gap: 0.5, mr: -0.5 }}>
                      <Tooltip title={t("dashboard_chat.query_history", "Query history")}>
                        <IconButton
                          onClick={() => nlChatRef.current?.clearAll()}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            bgcolor: alpha(theme.palette.common.white, 0.08),
                            color: alpha(theme.palette.common.white, 0.75),
                            border: `1px solid ${alpha(athenaColors.periwinkle, 0.35)}`,
                            transition: "all .2s",
                            "&:hover": {
                              bgcolor: alpha(athenaColors.periwinkle, 0.18),
                              color: theme.palette.common.white,
                              borderColor: alpha(theme.palette.primary.main, 0.5),
                            },
                          }}
                        >
                          <HistoryIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("dashboard.nl.send", "Send")}>
                        <span>
                          <IconButton
                            onClick={() => handleNlFollowupSubmit()}
                            disabled={!nlFollowupInput.trim()}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              // Same recipe as DashboardChat's send: royalBlue
                              // enabled, and a disabled state that is visible.
                              // text.primary is deepNavy, so at 25% the
                              // disabled arrow was an unreadable grey blob.
                              background: `linear-gradient(180deg, ${athenaColors.royalBlue} 0%, ${alpha(
                                athenaColors.royalBlue,
                                0.85,
                              )} 100%)`,
                              color: theme.palette.common.white,
                              border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                              transition: "all .25s ease",
                              "&:hover": {
                                background: `linear-gradient(180deg, ${alpha(
                                  athenaColors.royalBlue,
                                  0.9,
                                )} 0%, ${athenaColors.royalBlue} 100%)`,
                                transform: "translateY(-1px)",
                              },
                              "&.Mui-disabled": {
                                background: alpha(theme.palette.common.white, 0.08),
                                color: alpha(theme.palette.common.white, 0.35),
                                border: `1px solid ${alpha(
                                  theme.palette.common.white,
                                  0.2,
                                )}`,
                              },
                            }}
                          >
                            <SendIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  ...glassInputSx,
                  "& .MuiOutlinedInput-root": {
                    ...glassInputSx["& .MuiOutlinedInput-root"],
                    borderRadius: 2,
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>
            {/* Follow-up chips */}
            {nlFollowups.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", pl: 7 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.65),
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    mr: 0.5,
                  }}
                >
                  {t("dashboard_chat.or_continue", "Or continue with one of these:")}
                </Typography>
                {nlFollowups.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    clickable
                    onClick={() => handleNlChipFollowup(suggestion)}
                    sx={{
                      height: 24,
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      borderColor: alpha(athenaColors.periwinkle, 0.4),
                      color: theme.palette.common.white,
                      backgroundColor: alpha(theme.palette.common.white, 0.06),
                      "&:hover": {
                        background: alpha(athenaColors.periwinkle, 0.18),
                        borderColor: athenaColors.periwinkle,
                      },
                    }}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Top bar: Search + FAB                                              */}
        {/* Joan, 2026-05-27: in the DEFAULT dashboard view the row renders    */}
        {/* INLINE (inside the glass container). When V2 patient creation is   */}
        {/* active it portals to the LayoutContainer's "dashboard-floating-   */}
        {/* header" slot so it sits ABOVE the now-hidden glass container.     */}
        {/* NL has its own header (back + follow-up input) — search hidden.   */}
        {/* ----------------------------------------------------------------- */}
        <Box data-tour="patient-search-and-results">
        {!nlExpanded && (() => {
          // Joan, 2026-06-03 (cursor_logs/036 §16): we used to switch the
          // search row between a React Portal (when V2 form active) and
          // inline JSX (default). That structural flip happened EVERY time
          // the user started typing — the first keystroke flipped
          // isSearchActive → expandedAddPatient → portal/inline switch →
          // React unmounted the TextField → focus was lost → the next
          // keystroke landed on <body>. That's why typing "666" only got "6".
          //
          // Fix: always render the search row inline at its normal place
          // in the dashboard container. The TextField's DOM node stays
          // mounted across state changes, so focus is never lost. No
          // position-fixed pop-out — Joan flagged that as visually wrong
          // (full-viewport-wide bar with its own backdrop blur was off-
          // brand). The row simply sits at the top of the container in
          // both default and V2-form states.
          const searchRow = (
            <Box
              data-tour="patient-search-area"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  patientCount === 0 ? "center" : "flex-start",
                mb: 2,
                flexShrink: 0,
                gap: 2,
                pt: 0,
              }}
            >
              {/* Back arrow joins this row when V2 form is active. */}
              {expandedAddPatient && useFormV2 && (
                <DashboardBackButton
                  prominent
                  onClick={() => setExpandedAddPatient(false)}
                />
              )}

              {(patientCount ?? 0) > 0 && (
                <TextField
                  data-tour="patient-search"
                  fullWidth
                  size="small"
                  placeholder={t(
                    "dashboard.search_placeholder",
                    "Search patients to switch to list view...",
                  )}
                  value={searchQuery}
                  inputRef={searchInputRef}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: 20,
                            color: "primary.main",
                            opacity: 0.7,
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {SHORTCUT_KEYS.map((k) => (
                            <Box
                              key={k}
                              component="kbd"
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                fontFamily: "inherit",
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                lineHeight: 1.6,
                                color: alpha(theme.palette.common.white, 0.6),
                                backgroundColor: alpha(
                                  theme.palette.common.white,
                                  0.08,
                                ),
                                border: `1px solid ${alpha(
                                  theme.palette.common.white,
                                  0.16,
                                )}`,
                              }}
                            >
                              {k}
                            </Box>
                          ))}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    ...glassInputSx,
                    "& .MuiOutlinedInput-root": {
                      ...glassInputSx["& .MuiOutlinedInput-root"],
                      borderRadius: glassControl.radius,
                      // Matched to the CTA. At size="small" the field was 40
                      // next to a 48 button, which is what made the button
                      // look oversized rather than the field short.
                      height: glassControl.height,
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                      color: athenaColors.periwinkle,
                    },
                  }}
                />
              )}

              {/* Hidden while the create form is open: offering "Add patient"
                  from inside patient creation is a no-op the user has to
                  read and dismiss. Back takes over the slot instead. */}
              {canCreatePatients && !expandedAddPatient && (
                <Fab
                  data-tour="create-patient"
                  aria-label="add"
                  onClick={() => {
                    // When the user is in the middle of a search (image 20
                    // 2026-06-03), clicking + should clear the search and
                    // open the patient-create form — not append the form
                    // under stale search results.
                    if (searchQuery) {
                      setSearchQuery("");
                      setSearchHighlightIdx(-1);
                      setSearching(false);
                      if (searchTimerRef.current) {
                        clearTimeout(searchTimerRef.current);
                      }
                    }
                    setExpandedAddPatient(true);
                  }}
                  variant="extended"
                  sx={{
                    flexShrink: 0,
                    height: glassControl.height,
                    minHeight: glassControl.height,
                    px: 2.5,
                    borderRadius: glassControl.radius,
                    gap: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    whiteSpace: "nowrap",
                    // Orange, not royalBlue: on the dark ground the blue CTA is
                    // the same family as the panels behind it and stops
                    // reading as the one action on the screen. Ink is deepNavy,
                    // not white — white on brand orange is 2.59:1, the same
                    // failure the landing hero CTA was fixed for.
                    background: `linear-gradient(180deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
                    color: athenaColors.deepNavy,
                    boxShadow: `0 4px 16px ${alpha(athenaColors.orange, 0.3)}`,
                    "&:hover": {
                      background: `linear-gradient(180deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 16px ${alpha(athenaColors.orange, 0.42)}`,
                    },
                    border: "none",
                    transition: "all .2s ease",
                    "&:active": { transform: "translateY(0)" },
                    "&:focus-visible": {
                      outline: "none",
                      boxShadow: `0 0 0 3px ${alpha(
                        theme.palette.secondary.main,
                        0.5,
                      )}`,
                    },
                  }}
                >
                  <AddIcon />
                  {t("dashboard.add_patient", "Add patient")}
                </Fab>
              )}
            </Box>
          );
          return searchRow;
        })()}

        {/* Patient list — shown when searching */}
        <Collapse in={isSearchActive} timeout={300}>
          <NeumorphicCard
            sx={{
              m: 0,
              flex: 1,
              minHeight: 200,
              p: 1.5,
              ...glassListSx,
              backgroundColor: alpha(athenaColors.deepNavy, 0.45),
              backgroundImage: "none",
              border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
            }}
          >
            <Stack spacing={1} sx={{ height: "100%" }}>
              {patientsToShow.length > 0 ? (
                <>
                  <GlassBox onClick={() => setExpandedManage((prev) => !prev)}>
                    <Typography variant="h6">
                      {t("dashboard.manage_patients", "Manage Patients")} (
                      {patientsToShow.length})
                    </Typography>
                    <ExpandToggle expanded={expandedManage} />
                  </GlassBox>

                  <Collapse in={expandedManage}>
                    <Box
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 320px)",
                        transition: "max-height 0.3s ease",
                        borderRadius: 1,
                      }}
                    >
                      {patientsToShow.map((patient, idx) => (
                        <Box
                          key={patient.id}
                        onClick={() => handlePatientClick(patient)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 2,
                          py: 1.25,
                          borderBottom: `1px solid ${alpha(
                            theme.palette.common.white,
                            0.07
                          )}`,
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          // primary.main is deepNavy: on this dark card the row
                          // fill was invisible, so hovering appeared to
                          // highlight only the container (NeumorphicCard's own
                          // hover changes its border and nothing else).
                          bgcolor:
                            idx === searchHighlightIdx
                              ? alpha(athenaColors.periwinkle, 0.18)
                              : "transparent",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.common.white, 0.08),
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: "0 1 auto",
                                minWidth: 0,
                              }}
                            >
                              {revealedNames[patient.id] !== undefined
                                ? revealedNames[patient.id] ||
                                  t("dashboard.unnamed_patient", "Unnamed Patient")
                                : patient.name
                                  ? anonymizeName(patient.name)
                                  : t(
                                      "dashboard.unnamed_patient",
                                      "Unnamed Patient"
                                    )}
                            </Typography>
                            {/* external_id sibling — same line as the name,
                                tasteful muted treatment so duplicate-name patients
                                are still distinguishable at a glance. The value
                                is the serializer's masked form for non-PII
                                viewers; the eye-icon reveal hands back both name
                                AND external_id atomically. */}
                            {patient.external_id ? (
                              <Typography
                                variant="caption"
                                aria-label="external id"
                                sx={{
                                  color: alpha(theme.palette.text.secondary, 0.75),
                                  fontFamily: "monospace",
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  px: 0.75,
                                  py: 0.15,
                                  borderRadius: 0.75,
                                  backgroundColor: alpha(
                                    theme.palette.text.primary,
                                    0.05,
                                  ),
                                  flexShrink: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {patient.external_id}
                              </Typography>
                            ) : null}
                            {/* Reveal eye — same per-patient endpoint as the patient-details header,
                                audit-logged on every successful click. Gated on can_reveal_pii. */}
                            {patient.can_reveal_pii && (
                              <Tooltip
                                title={
                                  revealedNames[patient.id] !== undefined
                                    ? t("patient.hide_name_tooltip", "Hide raw name")
                                    : t(
                                        "patient.reveal_name_tooltip",
                                        "Reveal raw name (audit-logged)",
                                      )
                                }
                                arrow
                                placement="top"
                              >
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleRevealPatientName(e, patient)}
                                  disabled={revealingId === patient.id}
                                  aria-label={t(
                                    "patient.reveal_name_tooltip",
                                    "Reveal raw name (audit-logged)",
                                  )}
                                  sx={{
                                    p: 0.25,
                                    color: alpha(theme.palette.text.secondary, 0.8),
                                    flexShrink: 0,
                                    "&:hover": {
                                      color: theme.palette.text.primary,
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.12,
                                      ),
                                    },
                                  }}
                                >
                                  {revealingId === patient.id ? (
                                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                                  ) : revealedNames[patient.id] !== undefined ? (
                                    <VisibilityOff sx={{ fontSize: 16 }} />
                                  ) : (
                                    <Visibility sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {patient.environment_names &&
                            patient.environment_names.length > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.primary.main,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  opacity: 0.8,
                                }}
                              >
                                {patient.environment_names.join(", ")}
                              </Typography>
                            )}
                          {patient.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {patient.description}
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title={t("patients.delete", "Delete patient")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleDeleteClick(e, patient)}
                            sx={{
                              ml: 1,
                              opacity: 0.6,
                              "&:hover": {
                                opacity: 1,
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </>
              ) : searching ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 5,
                    gap: 1.25,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    {t("patients.searching", "Searching…")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 5,
                    gap: 0.75,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t("patients.no_results", "No patients found")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7 }}>
                    {t("patients.no_results_hint", "Try a different name or description")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </NeumorphicCard>
        </Collapse>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* Add Patient form (expandable)                                      */}
        {/* ----------------------------------------------------------------- */}
        <Collapse in={expandedAddPatient} unmountOnExit sx={{ flexShrink: 0 }}>
          {/* V2 patient creation — Joan, 2026-05-27: V2 is now the only
              form. V1 (AddPatientForm.tsx) stays in the repo as fallback
              but is no longer reachable from the dashboard. The wrapping
              VideoGlassContainer renders `inactive` because the form has
              its own canvas chrome. */}
          <VideoGlassContainer variant="light" inactive>
            <AddPatientFormV2
              newPatient={newPatient}
              setNewPatient={setNewPatient}
              availableEnvironments={availableEnvironments}
              selectedEnvIds={selectedEnvIds}
              setSelectedEnvIds={setSelectedEnvIds}
              onCreate={handleCreatePatient}
              onClose={() => setExpandedAddPatient(false)}
              onEnvironmentCreated={(env) => {
                setAvailableEnvironments((prev) => [
                  ...prev,
                  env as Environment,
                ]);
              }}
            />
          </VideoGlassContainer>
        </Collapse>

        {/* Empty state — video hero card when no patients exist */}
        {patientCount === 0 && !expandedAddPatient && (
          <WelcomeHeroCard
            onCreatePatient={() => setExpandedAddPatient(true)}
            onStartTutorial={() => tutorial.start()}
          />
        )}

        {/* ----------------------------------------------------------------- */}
        {/* Content area                                                       */}
        {/* ----------------------------------------------------------------- */}

        {/* Hero band + KPI cards — hidden when NL expanded or searching */}
        {!nlExpanded && !isSearchActive && !expandedAddPatient && (patientCount ?? 0) > 0 && (
          <DashboardHeroBand />
        )}

        {/* NL chat — Joan, 2026-05-27: VideoGlassContainer kept always
            INACTIVE. The expanded NL result card carries its own chrome
            (same family as the V2 patient creation form). No video bg, no
            dark gradient overlay. */}
        {!isSearchActive && !expandedAddPatient && (patientCount ?? 0) > 0 && (
          <Box
            sx={{
              mb: nlExpanded ? 0 : 0.75,
              // The concept gives the ask bar about half the width and leaves
              // the rest empty. It reads as one question rather than as a
              // full-bleed toolbar, and it stops competing with the KPI row
              // directly above it. Full width once results are expanded.
              ...(!nlExpanded && { maxWidth: { xs: "100%", lg: "56%" } }),
            }}
          >
            <VideoGlassContainer variant="light" inactive>
              <DashboardChat
                darkMode
                onActiveChange={handleNlActiveChange}
                onDismiss={nlExpanded ? handleNlDismiss : undefined}
                hideInputBar={nlExpanded}
                onFollowupsChange={handleNlFollowupsChange}
                ref={nlChatRef}
              />
            </VideoGlassContainer>
          </Box>
        )}

        {/* Analytics (charts) — hidden when NL expanded */}
        {!nlExpanded && !isSearchActive && !expandedAddPatient && (patientCount ?? 0) > 0 && (
          <Box sx={{ mb: 2 }}>
            <AnalyticsDashboard hideSummaryCards />
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          slotProps={{ backdrop: { sx: athenaBackdropSx } }}
          PaperProps={{ sx: athenaDarkPaperSx }}
        >
          <DialogTitle id="delete-dialog-title" sx={athenaDarkTitleSx}>
            {t("patients.delete_confirm_title", "Delete Patient?")}
          </DialogTitle>
          <DialogContent sx={athenaDarkContentSx}>
            <DialogContentText id="delete-dialog-description">
              {t(
                "patients.delete_confirm_message",
                "Are you sure you want to delete"
              )}{" "}
              <strong>
                {patientToDelete?.name
                  ? anonymizeName(patientToDelete.name)
                  : ""}
              </strong>
              ?{" "}
              {t(
                "patients.delete_confirm_warning",
                "This will also delete all related tables and data. This action cannot be undone."
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={athenaDarkActionsSx}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              sx={athenaDarkSecondaryButtonSx}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              autoFocus
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                backgroundColor: theme.palette.error.light,
                color: theme.palette.common.white,
                "&:hover": { backgroundColor: theme.palette.error.main },
              }}
            >
              {deleting
                ? t("common.deleting", "Deleting...")
                : t("common.delete", "Delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </EnvironmentFilterProvider>
  );
};

export default SystemAdminDashboard;
